import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserRole } from '@klaro7/shared-types';
import {
  Modal,
  ModalPortal,
  ModalOverlay,
  ModalClose,
  ModalTitle,
  ModalDescription,
  Alert,
  AlertDescription,
} from '@klaro7/ui';
import * as DialogPrimitive from '@radix-ui/react-dialog';

import { useAuth } from '../hooks/useAuth';
import { getDefaultRedirect } from '../context/AuthContext';
import { useRegistrationStatus } from '@/features/settings/hooks/useServicesSettings';
import {
  needsPhoneVerification,
  PHONE_VERIFICATION_PATH,
} from '@/features/phone-verification/utils/phoneGate';
import {
  needsEmailVerification,
  EMAIL_VERIFICATION_PATH,
} from '@/features/email-verification/utils/emailGate';

/* -------------------------------------------------------------------------- */
/*  Keyframes style tag – injected once                                       */
/* -------------------------------------------------------------------------- */

function ModalKeyframes() {
  return (
    <style>{`
      @keyframes klaro-twinkle {
        0%   { opacity: 0.2; transform: scale(0.6) rotate(0deg); }
        50%  { opacity: 1;   transform: scale(1.2) rotate(10deg); }
        100% { opacity: 0.4; transform: scale(0.8) rotate(-5deg); }
      }
      @keyframes klaro-line-fade {
        0%   { opacity: 0.05; }
        100% { opacity: 0.2; }
      }
      @keyframes klaro-shoot-1 {
        0%   { transform: translate(15%, 20%); opacity: 0; }
        5%   { opacity: 1; }
        15%  { transform: translate(85%, 55%); opacity: 0; }
        100% { transform: translate(85%, 55%); opacity: 0; }
      }
      @keyframes klaro-shoot-2 {
        0%   { transform: translate(70%, 10%); opacity: 0; }
        4%   { opacity: 0.8; }
        12%  { transform: translate(25%, 65%); opacity: 0; }
        100% { transform: translate(25%, 65%); opacity: 0; }
      }
      @keyframes klaro-gradient-shift {
        0%   { background-position: 0% 0%; }
        50%  { background-position: 100% 100%; }
        100% { background-position: 0% 0%; }
      }
      @keyframes klaro-modal-enter {
        from { opacity: 0; transform: translate(-50%, -50%) scale(0.95); }
        to   { opacity: 1; transform: translate(-50%, -50%) scale(1); }
      }
      @keyframes klaro-modal-exit {
        from { opacity: 1; transform: translate(-50%, -50%) scale(1); }
        to   { opacity: 0; transform: translate(-50%, -50%) scale(0.95); }
      }
      @keyframes klaro-overlay-in {
        from { opacity: 0; }
        to   { opacity: 1; }
      }
      @keyframes klaro-overlay-out {
        from { opacity: 1; }
        to   { opacity: 0; }
      }
      @keyframes klaro-underline-expand {
        from { transform: scaleX(0); }
        to   { transform: scaleX(1); }
      }
      @keyframes klaro-glow-pulse {
        0%, 100% { box-shadow: 0 0 15px rgba(24,153,170,0.15), 0 0 30px rgba(24,153,170,0.05); }
        50%      { box-shadow: 0 0 20px rgba(24,153,170,0.25), 0 0 40px rgba(24,153,170,0.1); }
      }
      @keyframes klaro-btn-shimmer {
        from { background-position: 200% center; }
        to   { background-position: -200% center; }
      }

      /* Input focus underline animation */
      .klaro-input-wrap {
        position: relative;
      }
      .klaro-input-wrap::after {
        content: '';
        position: absolute;
        bottom: 0;
        left: 50%;
        width: 100%;
        height: 2px;
        background: linear-gradient(90deg, #1899AA, #76B9B9);
        transform: translateX(-50%) scaleX(0);
        transform-origin: center;
        transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        border-radius: 1px;
        pointer-events: none;
        z-index: 1;
      }
      .klaro-input-wrap:focus-within::after {
        transform: translateX(-50%) scaleX(1);
      }
    `}</style>
  );
}

/* -------------------------------------------------------------------------- */
/*  LoginModal – split-panel design                                           */
/* -------------------------------------------------------------------------- */

export function LoginModal() {
  const navigate = useNavigate();
  const { login, showLoginModal, loginRedirectTo, closeLoginModal, loginModalContext } = useAuth();
  const { data: registrationStatus } = useRegistrationStatus();
  const registrationOpen = registrationStatus?.registrationEnabled ?? false;

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      const user = await login(email, password);
      const role = user?.role ?? UserRole.USER;
      // Telefono non verificato: l'OTP viene prima di qualsiasi redirect
      // (il redirect richiesto sarebbe comunque bloccato da ProtectedRoute).
      const to = loginRedirectTo || getDefaultRedirect(role);
      closeLoginModal();
      // Doppio opt-in: email prima del telefono.
      if (needsEmailVerification(user)) {
        navigate(EMAIL_VERIFICATION_PATH, { replace: true, state: { redirectTo: to } });
        return;
      }
      if (needsPhoneVerification(user)) {
        navigate(PHONE_VERIFICATION_PATH, { replace: true, state: { redirectTo: to } });
        return;
      }
      navigate(to, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login fallito. Riprova.');
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setError(null);
    setEmail('');
    setPassword('');
    setShowPassword(false);
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      closeLoginModal();
      resetForm();
    }
  };

  const handleRegisterClick = () => {
    const redirect = loginRedirectTo ? `?redirect=${encodeURIComponent(loginRedirectTo)}` : '';
    closeLoginModal();
    resetForm();
    navigate(`/register${redirect}`);
  };

  const handleForgotPasswordClick = () => {
    closeLoginModal();
    resetForm();
    navigate('/forgot-password');
  };

  return (
    <Modal open={showLoginModal && loginModalContext !== 'support'} onOpenChange={handleOpenChange}>
      <ModalPortal>
        {/* --- Custom overlay with teal tint + stronger blur --- */}
        <ModalOverlay
          className="bg-[#231F55]/30 backdrop-blur-lg"
        />

        {/* --- Custom content container (bypass ModalContent defaults) --- */}
        <DialogPrimitive.Content
          className="fixed left-[50%] top-[50%] z-50 w-[calc(100%-2rem)] max-w-[820px] translate-x-[-50%] translate-y-[-50%] overflow-hidden rounded-2xl border border-white/10 bg-[#1a1740] shadow-2xl focus:outline-none sm:w-[90vw]"
          style={{
            animation: 'klaro-modal-enter 0.35s cubic-bezier(0.16, 1, 0.3, 1) forwards, klaro-glow-pulse 4s ease-in-out 0.5s infinite',
          }}
        >
          <ModalKeyframes />

          {/* Visually hidden title + description for a11y */}
          <ModalTitle className="sr-only">Accedi a Klaro7</ModalTitle>
          <ModalDescription className="sr-only">
            Inserisci le tue credenziali per accedere alla piattaforma
          </ModalDescription>

          <div className="flex flex-col md:flex-row">
            {/* ================================================================ */}
            {/*  LEFT PANEL – Mystical visual (40%)                              */}
            {/* ================================================================ */}
            <div
              className="relative flex flex-col items-center justify-center overflow-hidden px-6 py-8 md:w-[40%] md:py-12"
              style={{
                background: 'linear-gradient(160deg, #231F55 0%, #1a1740 40%, #126f7d 100%)',
              }}
            >
              {/* Content */}
              <div className="relative z-10 flex flex-col items-center text-center">
                <img
                  src="/images/logo-clean.png"
                  alt="Klaro7"
                  className="mb-4 h-24 w-auto drop-shadow-lg md:mb-6 md:h-32"
                />

                <p className="font-sans text-lg font-light tracking-wide text-white/90 md:text-xl">
                  Il tuo percorso
                </p>
                <p className="font-sans text-lg font-light tracking-wide text-white/90 md:text-xl">
                  inizia qui
                </p>

                {/* Small decorative divider */}
                <div className="mt-4 flex items-center gap-2 md:mt-5">
                  <span className="h-px w-6 bg-white/20" />
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 12 12"
                    fill="none"
                    className="text-brand-cyan"
                    aria-hidden="true"
                  >
                    <path
                      d="M6 0L7.5 4.5L12 6L7.5 7.5L6 12L4.5 7.5L0 6L4.5 4.5L6 0Z"
                      fill="currentColor"
                      opacity="0.6"
                    />
                  </svg>
                  <span className="h-px w-6 bg-white/20" />
                </div>
              </div>
            </div>

            {/* ================================================================ */}
            {/*  RIGHT PANEL – Login form (60%)                                  */}
            {/* ================================================================ */}
            <div className="relative flex flex-col justify-center px-6 py-8 sm:px-10 md:w-[60%] md:px-12 md:py-14">
              {/* Close button */}
              <ModalClose className="absolute right-3 top-3 flex h-11 w-11 items-center justify-center rounded-full text-white/40 transition-colors hover:bg-white/10 hover:text-white/80 focus:outline-none focus:ring-2 focus:ring-brand-teal/40 focus:ring-offset-1 focus:ring-offset-[#1a1740] md:right-4 md:top-4">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M18 6 6 18" />
                  <path d="m6 6 12 12" />
                </svg>
                <span className="sr-only">Chiudi</span>
              </ModalClose>

              {/* Heading */}
              <div className="mb-6 md:mb-8">
                <h2 className="text-2xl font-semibold tracking-tight text-white md:text-3xl">
                  Bentornato
                </h2>
                <p className="mt-1.5 text-sm text-white/60 md:text-base">
                  Accedi per continuare la tua esperienza
                </p>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-5">
                {error && (
                  <Alert variant="error">
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                {/* Email field */}
                <div className="space-y-1.5">
                  <label
                    htmlFor="login-email"
                    className="block text-sm font-medium text-white/70"
                  >
                    Email
                  </label>
                  <div className="klaro-input-wrap rounded-lg">
                    <input
                      id="login-email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="nome@email.com"
                      required
                      autoComplete="email"
                      className="flex h-11 w-full rounded-lg border border-white/15 bg-white/[0.06] px-3.5 py-2 text-sm text-white placeholder:text-white/30 transition-colors duration-200 focus:border-brand-teal/50 focus:bg-white/[0.1] focus:outline-none focus:ring-2 focus:ring-brand-teal/20"
                    />
                  </div>
                </div>

                {/* Password field */}
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <label
                      htmlFor="login-password"
                      className="block text-sm font-medium text-white/70"
                    >
                      Password
                    </label>
                    <button
                      type="button"
                      onClick={handleForgotPasswordClick}
                      className="text-xs font-medium text-brand-teal transition-colors hover:text-brand-cyan hover:underline"
                    >
                      Password dimenticata?
                    </button>
                  </div>
                  <div className="klaro-input-wrap relative rounded-lg">
                    <input
                      id="login-password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="La tua password"
                      required
                      autoComplete="current-password"
                      className="flex h-11 w-full rounded-lg border border-white/15 bg-white/[0.06] px-3.5 py-2 pr-11 text-sm text-white placeholder:text-white/30 transition-colors duration-200 focus:border-brand-teal/50 focus:bg-white/[0.1] focus:outline-none focus:ring-2 focus:ring-brand-teal/20"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      aria-label={showPassword ? 'Nascondi password' : 'Mostra password'}
                      className="absolute right-1 top-1/2 z-10 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-md text-white/40 transition-colors hover:text-white/80 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-teal/40"
                    >
                      {showPassword ? (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden="true"
                        >
                          <path d="M10.733 5.076a10.744 10.744 0 0 1 11.205 6.575 1 1 0 0 1 0 .696 10.747 10.747 0 0 1-1.444 2.49" />
                          <path d="M14.084 14.158a3 3 0 0 1-4.242-4.242" />
                          <path d="M17.479 17.499a10.75 10.75 0 0 1-15.417-5.151 1 1 0 0 1 0-.696 10.75 10.75 0 0 1 4.446-5.143" />
                          <path d="m2 2 20 20" />
                        </svg>
                      ) : (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden="true"
                        >
                          <path d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0" />
                          <circle cx="12" cy="12" r="3" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>

                {/* Submit button */}
                <button
                  type="submit"
                  disabled={isLoading}
                  className="relative mt-2 flex h-11 w-full items-center justify-center gap-2 overflow-hidden rounded-lg font-medium text-white shadow-md transition-all duration-300 hover:shadow-lg hover:shadow-brand-teal/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-teal focus-visible:ring-offset-2 focus-visible:ring-offset-[#1a1740] active:scale-[0.98] disabled:pointer-events-none disabled:opacity-60"
                  style={{
                    background: 'linear-gradient(135deg, #1899AA 0%, #126f7d 100%)',
                  }}
                >
                  {isLoading ? (
                    <>
                      <svg
                        className="h-4 w-4 animate-spin"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      <span className="text-sm">Accesso in corso...</span>
                    </>
                  ) : (
                    <span className="text-sm">Accedi</span>
                  )}
                </button>
              </form>

              {/* Register link — only when registrations are open */}
              {registrationOpen && (
                <p className="mt-6 text-center text-sm text-white/40">
                  Non hai un account?{' '}
                  <button
                    type="button"
                    onClick={handleRegisterClick}
                    className="font-medium text-brand-teal transition-colors hover:text-brand-cyan hover:underline"
                  >
                    Registrati
                  </button>
                </p>
              )}
            </div>
          </div>
        </DialogPrimitive.Content>
      </ModalPortal>
    </Modal>
  );
}
